// Дясната част на „Разнасяне на отчети": разнасянето на ИЗБРАНИЯ отчет.
// Горе — „Отчет №X", мета (дата · затворен · сметки · сервитьори · оборот) и
// статус: кехлибарено „N продукта без склад", червено „N продукта не
// достигат", иначе зелено „Всичко е налично" (`detailStatusParts`). Броят
// сервитьори (имената при посочване) идва от разбивката на продаденото, която
// се зарежда паралелно и само информира; самата разбивка е при отчета — под
// картата му в списъка, а при `breakdownInline` (списъкът е лента, под 1024px)
// — сгъваем ред „Сервитьори и станции ▸" тук, под заглавието. После
// „Замяна с взаимозаменяеми продукти"; „Изписване от склад" (там се работи —
// първа) и сгъваемият панел „Продадено" (само рецептите): при контейнер
// ≥ 1040px е колона вдясно, по-тясно — свита лента над таблицата
// (`ReportPostingSoldCard`); долу футърът с Нето/Бруто, „N продукта ще
// останат на минус" и „Разнеси Отчет №X" (Ctrl/Cmd+Enter от всяка точка).
//
// Потвърждение се иска САМО при оставащ недостиг. Запазва се без промяна:
// изписването е ВИНАГИ FIFO; продукт без склад блокира записа ПРЕДИ
// потвърждението; 400 „Рецептите са променени" презарежда прегледа; датата на
// движението е датата на отчета.
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { App, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  useCreateReportPosting,
  useInventorySettings,
  useProducts,
  useReportPostingPreview,
} from "../../../../hooks/useInventory";
import { useReportSalesBreakdown } from "../../../../hooks/useInventorySoldAnalysis";
import type {
  PendingReport,
  ReportPosting,
  ReportSubstitutionItem,
} from "../../../../api/inventory";
import { hasOpenOverlay, isEditableTarget } from "../../../../lib/overlay";
import { formatQty, useUnitDecimals } from "../../components/qty";
import { formatMoney } from "../../components/numbers";
import { PRIMARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { StatusPill } from "../../components/list/ListBits";
import { KbdChip } from "../../components/KbdChip";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { useReportPostingWorksheetState } from "./ReportPostingWorksheetState";
import { ReportPostingSoldCard } from "./ReportPostingSoldCard";
import { ReportPostingWriteoffCard } from "./ReportPostingWriteoffCard";
import { ReportPostingWarehouseHint } from "./ReportPostingWarehouseHint";
import { ReportPostingSubstitutions } from "./ReportPostingSubstitutions";
import {
  ReportPostingShortageConfirmModal,
  type ShortageItem,
} from "./ReportPostingShortageConfirmModal";
import { buildGrossFactorOf } from "./reportPostingShared";
import { waiterNamesText } from "./reportPostingSalesBreakdownModel";
import { detailStatusParts } from "./pendingReportStatus";
import { PendingReportBreakdown } from "./PendingReportBreakdown";

type Props = {
  report: PendingReport;
  // разбивката по сервитьори и станции тук (списъкът с отчети е лента и няма
  // колона, под която да стои)
  breakdownInline?: boolean;
  // досегашните замени при „Коригирай и разнеси повторно"
  prefillSubstitutions?: ReportSubstitutionItem[];
  onPosted: (saved: ReportPosting, report: PendingReport) => void;
  // незапазени промени (избрани/сменени замени, складове, количества)
  onDirtyChange: (dirty: boolean) => void;
  onLeaveModule: () => void;
};

export const ReportPostingDetail = ({
  report,
  breakdownInline = false,
  prefillSubstitutions,
  onPosted,
  onDirtyChange,
  onLeaveModule,
}: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { isNarrow } = useInventoryShell();
  const previewQuery = useReportPostingPreview(report.id);
  const preview = previewQuery.data;
  // Разбивката по сервитьори/станции — паралелно, не блокира „Разнеси"; при
  // грешка просто я няма (само информация)
  const breakdown = useReportSalesBreakdown(report.id).data ?? null;
  const createMutation = useCreateReportPosting();

  const { data: products = [] } = useProducts();
  const { data: settings } = useInventorySettings();
  const vatRegistered = settings?.vatRegistered ?? true;
  const grossFactorOf = useMemo(
    () => buildGrossFactorOf(products, vatRegistered),
    [products, vatRegistered],
  );
  const decimalsOf = useUnitDecimals();

  const dirtyRef = useRef(false);
  const touch = () => {
    if (dirtyRef.current) return;
    dirtyRef.current = true;
    onDirtyChange(true);
  };
  const state = useReportPostingWorksheetState({ preview, grossFactorOf, onTouch: touch });

  // Инициализация от прегледа — точно веднъж, когато данните са свежи
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!preview || previewQuery.isFetching || initializedRef.current) return;
    if (preview.posted) return;
    initializedRef.current = true;
    state.initializeFromPreview(preview, prefillSubstitutions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, previewQuery.isFetching]);

  // ─── Недостигът, който остава след замените ──────────────────────────────
  const remaining: ShortageItem[] = (preview?.products ?? [])
    .filter((product) => (state.shortageByProduct.get(product.productId) ?? 0) > 0)
    .map((product) => ({
      key: String(product.productId),
      name: product.productName,
      missing: `${formatQty(
        state.shortageByProduct.get(product.productId) ?? 0,
        decimalsOf(product.productId),
      )}${product.unit ? ` ${product.unit}` : ""}`,
    }));
  const substitutedCount = state.substitutions.length;
  // Без склад (няма предизбор и не е избран) — отделно от недостига: не е
  // „не достига", нито „налично"; „Разнеси" го спира с „Изберете склад за: …"
  const withoutWarehouseCount = state.productsWithoutWarehouse().length;

  // ─── Запис ────────────────────────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const post = () => {
    if (!preview) return;
    createMutation.mutate(
      {
        reportId: preview.reportId,
        date: dayjs(preview.date ?? preview.reportDate).toISOString(),
        lines: state.buildLines(),
        substitutions: state.substitutions,
      },
      {
        onSuccess: (saved) => {
          setConfirmOpen(false);
          dirtyRef.current = false;
          onDirtyChange(false);
          onPosted(saved, report);
        },
        onError: (e: unknown) => {
          setConfirmOpen(false);
          const err = e as { response?: { status?: number; data?: { detail?: string } } };
          message.error(err.response?.data?.detail || t("inventoryPage.postingErr"));
          // Рецептите са сменени след прегледа → зареждаме го наново
          if (err.response?.status === 400) {
            initializedRef.current = false;
            qc.invalidateQueries({
              queryKey: ["inventory", "report-posting-preview", report.id],
            });
          }
        },
      },
    );
  };
  const submit = () => {
    if (!preview || createMutation.isPending) return;
    const missingWarehouse = state.productsWithoutWarehouse();
    if (missingWarehouse.length > 0) {
      message.warning(
        t("inventoryPage.postingMissingWarehouse", {
          names: missingWarehouse.map((p) => p.productName).join(", "),
        }),
      );
      return;
    }
    // Нулевият отчет (нищо за изписване) СЕ разнася — празно движение; иначе
    // празен запис значи, че нещо не е наред с прегледа
    if (!empty && state.buildLines().length === 0 && state.substitutions.length === 0) {
      message.warning(t("inventoryPage.postingNoRecipes"));
      return;
    }
    if (remaining.length > 0) setConfirmOpen(true);
    else post();
  };

  // ─── Клавиши: Ctrl/Cmd+Enter = Разнеси; Esc „отникъде" при промени ───────
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const leaveRef = useRef(onLeaveModule);
  leaveRef.current = onLeaveModule;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || hasOpenOverlay()) return;
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        event.preventDefault();
        submitRef.current();
        return;
      }
      // Без незапазени замени Esc минава към глобалната навигация „назад"
      if (event.key === "Escape" && dirtyRef.current && !isEditableTarget(event.target)) {
        event.preventDefault();
        leaveRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const warehouseNameOf = (productId: number, warehouseId: number) =>
    state.warehouseOptionsOf.get(productId)?.find((o) => o.value === warehouseId)?.label ??
    `#${warehouseId}`;

  const waitersCount = breakdown?.waiters.length ?? 0;
  const meta: ReactNode[] = [
    dayjs(report.reportDate).format("DD.MM.YYYY"),
    report.closedAt
      ? t("inventoryPage.postingMetaClosed", { time: dayjs(report.closedAt).format("HH:mm") })
      : null,
    t("inventoryPage.postingPickBills", { count: report.totalBillsCount }),
    breakdown && waitersCount > 0 ? (
      <Tooltip title={waiterNamesText(breakdown)} trigger={["hover", "click"]}>
        <span className="cursor-help underline decoration-shade-4 decoration-dotted underline-offset-[3px]">
          {t("inventoryPage.postingMetaWaiters", { count: waitersCount })}
        </span>
      </Tooltip>
    ) : null,
    t("inventoryPage.postingMetaTurnover", { amount: formatMoney(report.totalAmount) }),
  ].filter(Boolean);

  const ready = !!preview && !preview.posted && initializedRef.current;
  const empty = ready && preview.products.length + preview.recipes.length === 0;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div
        className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto ${
          isNarrow ? "px-3 py-3" : "px-6 py-4"
        }`}
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col gap-[3px]">
            <span className="text-[19px] font-semibold leading-[1.2] text-ink">
              {t("inventoryPage.postingReportTitle", { number: report.reportNumber })}
            </span>
            <span className="text-[13px] leading-[normal] text-body">
              {meta.map((part, index) => (
                <Fragment key={index}>
                  {index > 0 && " · "}
                  {part}
                </Fragment>
              ))}
            </span>
          </div>
          {/* Хапчетата са група вдясно — при пренасяне минават заедно на нов ред */}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
            {report.annulledPostingId != null && (
              <StatusPill tone="neutral">
                {t("inventoryPage.postingCorrectsAnnulled", { number: report.annulledPostingId })}
              </StatusPill>
            )}
            {ready &&
              !empty &&
              detailStatusParts(
                {
                  noWarehouseCount: withoutWarehouseCount,
                  shortCount: remaining.length,
                  substitutedCount,
                },
                t,
              ).map((part) =>
                part.tone === "brand" ? (
                  <Tooltip key={part.tone} title={t("inventoryPage.postingNoWarehouseHint")}>
                    <span data-status-pill={part.tone} className="cursor-help">
                      <StatusPill tone={part.tone} dot>
                        {part.text}
                      </StatusPill>
                    </span>
                  </Tooltip>
                ) : (
                  <span key={part.tone} data-status-pill={part.tone}>
                    <StatusPill tone={part.tone} dot>
                      {part.text}
                    </StatusPill>
                  </span>
                ),
              )}
          </div>
        </div>

        {breakdownInline && <PendingReportBreakdown reportId={report.id} variant="inline" />}

        {!ready ? (
          <div className="flex justify-center p-16">
            <Spin size="large" />
          </div>
        ) : empty ? (
          <div className="flex flex-col items-center gap-1.5 rounded-xl border border-shade-2 bg-panel px-6 py-12 text-center">
            <span className="text-[15px] font-semibold text-ink">{t("inventoryPage.postingNoRecipes")}</span>
            <span className="max-w-[520px] text-[13px] leading-[1.5] text-body">
              {t("inventoryPage.postingZeroHint")}
            </span>
          </div>
        ) : (
          <>
            <ReportPostingSubstitutions
              issues={state.substitutionIssues}
              warehouseNameOf={warehouseNameOf}
              decimalsOf={decimalsOf}
              onChange={state.setSubstitution}
            />
            {/* Контейнерът решава подредбата (не екранът): менюто и списъкът
                отляво менят ширината на детайла. ≥ 1040px — изписване + колона
                „Продадено" 280px вдясно; иначе „Продадено" е свита лента
                отгоре (order-first), а в DOM изписването остава първо. */}
            <div className="@container">
              <div className="flex flex-col gap-3 @min-[1040px]:flex-row @min-[1040px]:items-start">
                <div className="min-w-0 @min-[1040px]:flex-1">
                  <ReportPostingWriteoffCard
                    products={preview.products}
                    state={state}
                    grossFactorOf={grossFactorOf}
                    decimalsOf={decimalsOf}
                    renderWarehouseAddon={(product) => (
                      <ReportPostingWarehouseHint
                        product={product}
                        warehouseIds={(state.allocations[product.productId] ?? []).map(
                          (allocation) => allocation.warehouseId,
                        )}
                        warehouseNameOf={(warehouseId) =>
                          warehouseNameOf(product.productId, warehouseId)
                        }
                      />
                    )}
                  />
                </div>
                <div className="order-first min-w-0 @min-[1040px]:order-none @min-[1040px]:w-[280px] @min-[1040px]:shrink-0">
                  <ReportPostingSoldCard soldItems={preview.recipes} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {ready && (
        <footer
          className={`flex shrink-0 flex-wrap items-center border-t border-shade-2 bg-panel ${
            isNarrow
              ? "gap-4 px-3.5 py-3 pb-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)]"
              : "gap-6 px-6 py-3.5"
          }`}
        >
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] uppercase leading-[normal] tracking-[0.1em] text-muted">
              {t("inventoryPage.colNet")}
            </span>
            <span className="font-sheet text-xl font-semibold leading-[normal] text-ink tabular-nums">
              {formatMoney(state.totalNet)}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] uppercase leading-[normal] tracking-[0.1em] text-muted">
              {t("inventoryPage.colGross")}
            </span>
            <span className="font-sheet text-xl font-bold leading-[normal] text-brand-deep-ink tabular-nums">
              {formatMoney(state.totalGross)}
            </span>
          </div>
          <div className="flex-1" />
          {remaining.length > 0 && (
            <span className="text-[13px] leading-[normal] text-danger-ink">
              {t("inventoryPage.postingRemaining", { count: remaining.length })}
            </span>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={createMutation.isPending}
            className={`${PRIMARY_BUTTON_CLASS} !px-[22px] !py-3 !text-sm ${
              isNarrow ? "flex-[1_1_100%]" : ""
            }`}
          >
            {t("inventoryPage.postingSubmitReport", { number: report.reportNumber })}
            {!isNarrow && <KbdChip>Ctrl+Enter</KbdChip>}
          </button>
        </footer>
      )}

      <ReportPostingShortageConfirmModal
        open={confirmOpen}
        reportNumber={report.reportNumber}
        items={remaining}
        saving={createMutation.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={post}
      />
    </div>
  );
};

export default ReportPostingDetail;
